import styled, { keyframes } from "styled-components";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 40px;
  background: var(--background);
  min-height: 100vh;
  font-family: "Inter", sans-serif;

  @media (max-width: 768px) {
    padding: 20px;
    gap: 16px;
  }
`;

export const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 16px;
  color: var(--text-secondary);

  .spinner {
    animation: ${spin} 1s linear infinite;
    color: var(--primary);
  }
`;

export const BusinessHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 640px) {
    align-items: flex-start;
  }

  .header-info {
    .breadcrumb {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 11px;
      color: var(--text-secondary);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 8px;
    }

    h1 {
      font-size: clamp(20px, 5vw, 28px);
      color: var(--text-main);
      font-weight: 800;
      margin: 0;
    }

    p {
      color: var(--text-secondary);
      font-size: 14px;
      margin-top: 4px;
    }
  }

  .sync-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    color: var(--text-main);
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: 0.2s;

    &:hover {
      background: #f8fafc;
      border-color: var(--primary);
    }

    @media (max-width: 480px) {
      width: 100%;
      justify-content: center;
    }
  }
`;

export const EnterpriseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;

  @media (min-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)) 300px;
  }
`;

export const MetricCard = styled.div`
  background: var(--surface);
  padding: 20px;
  border-radius: var(--radius-md);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: 15px;

  .card-top {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .badge-live {
      background: #fee2e2;
      color: #ef4444;
      font-size: 10px;
      font-weight: 800;
      padding: 2px 8px;
      border-radius: 4px;
    }

    .trend {
      font-size: 11px;
      font-weight: 700;
      color: var(--text-secondary);
      &.positive {
        color: #16a34a;
      }
      &.negative {
        color: #ef4444;
      }
    }
  }

  .card-body {
    label {
      font-size: 13px;
      color: var(--text-secondary);
      font-weight: 500;
    }

    h3 {
      font-size: 24px;
      color: var(--text-main);
      margin: 4px 0;
      display: flex;
      align-items: baseline;
      gap: 6px;

      small {
        font-size: 12px;
        color: var(--text-secondary);
        font-weight: 400;
      }
    }

    .sub-text {
      font-size: 11px;
      color: var(--text-secondary);
    }
  }

  .progress-bar-bg {
    height: 6px;
    background: #f1f5f9;
    border-radius: 3px;
    overflow: hidden;
    margin-top: 8px;

    .progress-fill {
      height: 100%;
      background: var(--primary);
      transition: width 0.5s ease;
      &.critical {
        background: #ef4444;
      }
    }
  }
`;

export const QuickActionsBox = styled.div`
  background: #1e293b;
  padding: 20px;
  border-radius: var(--radius-md);
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 160px;

  h4 {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 15px;
    opacity: 0.9;
  }

  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;

    button {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px;
      border-radius: 8px;
      border: none;
      font-weight: 600;
      font-size: 13px;
      cursor: pointer;
      transition: 0.2s;

      &.btn-primary {
        background: var(--primary);
        color: white;
        &:hover {
          background: var(--primary-hover);
        }
      }

      &.btn-secondary {
        background: rgba(255, 255, 255, 0.1);
        color: white;
        &:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      }
    }
  }
`;

export const MainDashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 24px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const SectionBox = styled.section`
  background: var(--surface);
  border-radius: var(--radius-md);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
  overflow: hidden;

  .section-header {
    padding: 20px;
    border-bottom: 1px solid var(--border);
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 16px;
      color: var(--text-main);
      margin: 0;
    }
  }
`;

export const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
  }

  .enterprise-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;

    th {
      text-align: left;
      padding: 15px 20px;
      font-size: 11px;
      text-transform: uppercase;
      color: var(--text-secondary);
      background: #f8fafc;
      font-weight: 700;
    }

    td {
      padding: 16px 20px;
      border-bottom: 1px solid #f1f5f9;
      font-size: 14px;
      color: var(--text-main);
      white-space: nowrap;
    }

    tr:hover {
      background: #fcfdfe;
    }
  }
`;

export const EnterpriseBadge = styled.span`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 800;
  display: inline-block;
  background: ${(props) => {
    switch (props.status?.toLowerCase()) {
      case "preparando":
        return "#fff7ed";
      case "pendente":
        return "#f1f5f9";
      case "finalizado":
        return "#f0fdf4";
      default:
        return "#f1f5f9";
    }
  }};
  color: ${(props) => {
    switch (props.status?.toLowerCase()) {
      case "preparando":
        return "#f97316";
      case "pendente":
        return "#64748b";
      case "finalizado":
        return "#16a34a";
      default:
        return "#64748b";
    }
  }};
`;

export const SidePanel = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 1100px) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
`;

export const InsightCard = styled.div`
  background: var(--surface);
  padding: 20px;
  border-radius: var(--radius-md);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);

  h4 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: var(--text-main);
    margin-bottom: 20px;
  }

  .ranking-enterprise {
    display: flex;
    flex-direction: column;
    gap: 15px;

    .rank-row {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .rank-info {
      display: flex;
      justify-content: space-between;
      font-size: 13px;
      color: var(--text-main);

      strong {
        color: var(--primary);
      }
    }

    .rank-bar-mini {
      height: 4px;
      background: #f1f5f9;
      border-radius: 2px;
      overflow: hidden;

      div {
        height: 100%;
        transition: width 1s ease-out;
      }
    }
  }

  .empty-text {
    font-size: 12px;
    color: var(--text-secondary);
    text-align: center;
    padding: 10px;
  }
`;
